import { LanguageSwitcher, useTranslation } from '@jq4race/i18n';
import { Button } from '@jq4race/ui';
import logoPrimary from '@jq4race/ui/assets/logos/logo-primary.png';
import { LogOut, MapPin } from 'lucide-react';
import type { ReactNode } from 'react';

import type { Identity } from '../../auth/domain/resolve-identity';
import { BottomNav } from './BottomNav';
import { ConnectivityBadge } from './ConnectivityBadge';

export interface AppShellProps {
  locationName?: string;
  userEmail?: string;
  onSignOut: () => void;
  children: ReactNode;
  /**
   * pwa-offline-first Slice 6 (coordinator finding #4) — resolved ONCE by
   * `_authenticated.tsx` and threaded down to `ConnectivityBadge`/`BottomNav`
   * so neither has to resolve session/offline-profile context itself.
   */
  identity: Identity | null;
}

export function AppShell({ locationName, onSignOut, identity, children }: AppShellProps) {
  const { t } = useTranslation('shell');

  return (
    <>
      {/* Top bar */}
      <header
        className="sticky top-0 z-20 border-b border-border bg-background/90 backdrop-blur-md"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="mx-auto flex h-16 max-w-md items-center justify-between px-5">
          {/* Left: logo */}
          <div className="flex items-center">
            <img src={logoPrimary} alt="JQRACE4" className="h-10 w-auto" />
          </div>

          {/* Right: connectivity badge + location chip + language switcher + sign-out */}
          <div className="flex items-center gap-2">
            <ConnectivityBadge identity={identity} />
            {locationName ? (
              <div className="flex items-center gap-1.5">
                <MapPin className="size-4 shrink-0 text-jq-gold-bright" strokeWidth={1.5} />
                <span className="jq-body-sm max-w-[120px] truncate">{locationName}</span>
              </div>
            ) : null}
            <LanguageSwitcher />
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('signOutAria')}
              onClick={onSignOut}
              className="h-12 w-12"
            >
              <LogOut className="size-6" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </header>

      {/* Page content */}
      <main className="mx-auto max-w-md min-h-dvh px-5 pt-6 pb-28">{children}</main>

      {/* Bottom navigation */}
      <BottomNav identity={identity} />
    </>
  );
}
